/** `traverse.ladder` — climbing. A ladder is any entity carrying the
 * configured tag; its box spans the climb (top = `y - h`, bottom = `y`).
 * Holding up while overlapping it below the top, or down while standing on
 * its top rung, grabs it: the climber snaps to the ladder's centre and the
 * module HOLDS the body (the `heldBy` protocol pauses the platformer) while
 * up/down move it. Left/right do nothing on a ladder — the climber leaves
 * only at the top or bottom rung, standing on whatever surface is there.
 * Publishes both directions of every ladder as solver navigation links. */
import { id, num, obj } from '../schemaBuilders.js';
import { defineModule, str, type NavLinkV1 } from '../module.js';
import { clamp, heldBy, supportSurface, surfaces } from '../physics.js';
import { deltaX, type EntityV1 } from '../world.js';
import type { JsonObject } from '../../json.js';

export type LadderConfig = {
  ladderTag: string;
  /** Climb speed (units/s). */
  speed: number;
  /** Horizontal distance from a ladder's centre within which it can be grabbed. */
  grabReach: number;
};

const LEVEL_EPS = 0.06;

function span(l: EntityV1): { top: number; bottom: number } {
  return { top: l.y - l.h, bottom: l.y };
}

function release(d: JsonObject): void {
  d.hold = false; d.ladder = null;
}

export const ladder = defineModule<LadderConfig>({
  id: 'traverse.ladder', version: 1, attach: 'entity', role: 'behaviour', surfaceContact: true,
  summary: 'Climb ladder-tagged entities with up/down while overlapping them; the climber leaves only at the top or bottom rung.',
  config: obj({ ladderTag: id(), speed: num(0.5, 20), grabReach: num(0.05, 2) }),
  requires: ['body.platformer'], events: ['ladder-grab', 'ladder-exit'],
  presentation: { 'ladder-grab': { cue: 'none' }, 'ladder-exit': { cue: 'none' } },
  proofs: ['grab-from-below-climbs-to-top', 'grab-from-top-descends', 'out-of-reach-ignored', 'no-sideways-exit', 'pauses-mid-ladder', 'climb-state'],
  animStates: () => ['climb'],
  init(ctx, e) { release(ctx.data(e)); },
  systems: {
    think(ctx, e) {
      if (!e) return;
      const c = ctx.config, d = ctx.data(e), world = ctx.world;
      if (e.status !== 'active') { release(d); return; }
      if (heldBy(e, 'traverse.ladder')) return;
      const input = ctx.input(e.slot ?? 0);
      let held = d.hold === true ? ctx.entity(str(d, 'ladder')) : undefined;
      if (d.hold === true && !held) release(d);
      if (!held) {
        if (input.dy === 0) return;
        held = ctx.entities({ tag: c.ladderTag }).find(l => {
          if (Math.abs(deltaX(world, l.x, e.x)) > c.grabReach) return false;
          const { top, bottom } = span(l);
          return input.dy < 0 ? e.y > top + LEVEL_EPS && e.y <= bottom + LEVEL_EPS : e.grounded && Math.abs(e.y - top) <= LEVEL_EPS;
        });
        if (!held) return;
        d.hold = true; d.ladder = held.id;
        e.x = held.x; e.vx = 0; e.vy = 0; e.grounded = false; e.groundId = null;
        ctx.emit('ladder-grab', { x: e.x, y: e.y, slot: e.slot ?? 0 });
      }
      const { top, bottom } = span(held);
      e.vx = 0; e.vy = input.dy * c.speed;
      e.y = clamp(e.y + input.dy * c.speed * ctx.dt, top, bottom);
      if ((input.dy < 0 && e.y <= top) || (input.dy > 0 && e.y >= bottom)) {
        release(d);
        e.vy = 0;
        const ground = supportSurface(world, surfaces(ctx), e.x, e.w / 4, e.y);
        e.grounded = ground !== null; e.groundId = ground?.entity.id ?? null;
        ctx.emit('ladder-exit', { x: e.x, y: e.y, slot: e.slot ?? 0 });
      }
    },
    present(ctx, e) {
      if (e && e.status === 'active' && ctx.data(e).hold === true) ctx.setAnim(e, 'climb');
    },
  },
  navLinks(ctx, e, player) {
    if (!e || e.id !== player.id) return [];
    const links: NavLinkV1[] = [];
    for (const l of ctx.entities({ tag: ctx.config.ladderTag })) {
      const { top, bottom } = span(l);
      const cost = (bottom - top) * 1.5;
      links.push({ from: { x: l.x, y: bottom }, to: { x: l.x, y: top }, cost, dy: -1, along: true, label: 'ladder' });
      links.push({ from: { x: l.x, y: top }, to: { x: l.x, y: bottom }, cost, dy: 1, along: true, label: 'ladder' });
    }
    return links;
  },
});
